/**
 * Draft / confirm booking — same payloads as NewBooking.jsx (customer API).
 */
import config from '../config';
import { calculateB2bBookingPrice } from './b2bPricing';
import { discountFareAboveBase } from './discountFloor';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export function formatLocalDateTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function getBookingType(pickupDateTime, isFlexBooking) {
  if (isFlexBooking) return 'flex';
  const now = new Date();
  const hoursDiff = (pickupDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursDiff > 2 ? 'scheduled' : 'instant';
}

export function defaultPickupTime() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 15);
  d.setSeconds(0, 0);
  return d;
}

export function normalizeDiscountPercent(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(100, n);
}

/** Same as NewBooking computePaymentEstimate. */
export function computePaymentEstimate(vehicleOption, discountPercent, isB2b = false, hasGstin = false) {
  if (!vehicleOption) return 0;
  const pct = normalizeDiscountPercent(discountPercent);
  if (isB2b) {
    return calculateB2bBookingPrice(vehicleOption, pct).grandTotal;
  }
  const toll = parseFloat(vehicleOption.toll_price_applied || 0);
  const quoted = discountFareAboveBase(
    parseFloat(vehicleOption.estimated_price || 0),
    pct,
    toll,
    parseFloat(vehicleOption.base_price) || 0,
  );
  if (hasGstin) return Math.ceil(quoted.total * 1.05);
  return quoted.total;
}

function parseApiError(res, body) {
  let detail = body?.detail ?? body?.message;
  if (typeof detail === 'object') detail = detail.message || JSON.stringify(detail);
  if (detail) return String(detail);
  return `Request failed (${res.status})`;
}

/** Same shape as NewBooking handleConfirmBooking / handleSaveDraft */
export function buildBookingPayload({
  customerId,
  pickupTime,
  pickupAddressId,
  dropAddressIds,
  vehicleOption,
  isFlexBooking,
  deliveryDeadline,
  remarks,
  materialDescription,
  discountFactor = 0,
  hasGstin = false,
  isB2b = false,
  sourceLeadId = null,
  state = 'Pending',
  paymentOverride = '',
}) {
  const computed = computePaymentEstimate(vehicleOption, discountFactor, isB2b, hasGstin);
  const parsed = parseFloat(paymentOverride);
  const estimate = (
    paymentOverride !== '' && Number.isFinite(parsed) && parsed >= 0
  ) ? parsed : computed;
  const payload = {
    customer_id: parseInt(customerId, 10),
    pickup_time: formatLocalDateTime(pickupTime),
    pickup_address_id: parseInt(pickupAddressId, 10),
    drop_address_ids: dropAddressIds.map((id) => parseInt(id, 10)),
    vehicle_class_id: parseInt(vehicleOption.vehicle_class, 10),
    remarks: remarks || '',
    material_description: (materialDescription || '').trim() || null,
    delivery_deadline: deliveryDeadline ? formatLocalDateTime(deliveryDeadline) : null,
    booking_type: getBookingType(pickupTime, isFlexBooking),
    state,
    distance: parseFloat(vehicleOption.distance_km),
    duration: Math.round(vehicleOption.route_duration_mins),
    toll_charge: parseFloat(vehicleOption.toll_price_applied || 0),
    payment_estimate: estimate,
    actual_bill: estimate,
    eta: Math.round(vehicleOption.route_duration_mins),
    payment_id: null,
    discount_factor: (() => {
      if (isB2b) {
        return calculateB2bBookingPrice(vehicleOption, discountFactor).discountPercentage || 0;
      }
      const toll = parseFloat(vehicleOption.toll_price_applied || 0);
      const quoted = discountFareAboveBase(
        parseFloat(vehicleOption.estimated_price || 0),
        discountFactor,
        toll,
        parseFloat(vehicleOption.base_price) || 0,
      );
      if (quoted.discountAmount <= 0) return 0;
      return quoted.cappedAtBase ? quoted.effectivePercent : normalizeDiscountPercent(discountFactor);
    })(),
    source: 'ops',
    source_lead_id: sourceLeadId,
  };
  return payload;
}

export async function createBooking(payload) {
  const res = await fetch(`${config.customerApiBase}/create-booking`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseApiError(res, data));
  return data;
}

export async function saveDraftBooking(payload) {
  const res = await fetch(`${config.customerApiBase}/draft-booking`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseApiError(res, data));
  return data;
}

export async function fetchCustomerDetails(customerId) {
  if (!customerId) return null;
  const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
  const res = await fetch(`${config.customerApiBase}/customers/${customerId}/details`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data) return null;
  const c = data.customer || data;
  return {
    ...data,
    discount_percent: c.discount_percentage ?? c.discount_percent,
    discount_factor: c.discount_percentage ?? c.discount_factor,
    booking_count: Number(c.booking_count ?? 0),
    gstin: c.gstin || c.gst_in || '',
    is_b2b: Boolean(c.is_b2b || c.b2b_customer),
  };
}
